import React from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';

export type BrokerId = 'alpaca' | 'more-coming';

interface Broker {
  id: BrokerId;
  name: string;
  description: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
}

interface BrokerSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBrokerSelect: (brokerId: BrokerId) => void;
}

const brokers: Broker[] = [
  {
    id: 'alpaca',
    name: 'Alpaca',
    description: 'Connect to Alpaca for commission-free trading with API access',
    icon: (
      <div className="bg-yellow-300 rounded-full h-10 w-10 flex items-center justify-center">
        <img 
          src="/alpaca-logo.svg" 
          alt="Alpaca Logo" 
          className="w-8 h-8 object-contain"
        />
      </div>
    )
  },
  {
    id: 'more-coming',
    name: 'More Brokers Coming Soon',
    description: 'Our team is working to bring more brokers online. Stay tuned!',
    icon: (
      <div className="bg-gray-100 rounded-full h-10 w-10 flex items-center justify-center">
        <AlertCircle className="h-6 w-6 text-gray-500" />
      </div>
    ),
    comingSoon: true
  }
];

export const BrokerSelectionDialog: React.FC<BrokerSelectionDialogProps> = ({
  open,
  onOpenChange,
  onBrokerSelect
}) => {
  const handleBrokerClick = (brokerId: BrokerId, comingSoon?: boolean) => {
    if (brokerId === 'alpaca') {
      // Open Alpaca website in a new tab
      window.open('https://alpaca.markets/', '_blank');
    } else if (!comingSoon) {
      // Handle normal selection for other brokers that aren't coming soon
      onBrokerSelect(brokerId);
    }
    // Do nothing for coming soon options, just keep the dialog open
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Select Broker</DialogTitle>
          <DialogDescription>
            Choose a broker to connect your trading account
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 gap-4 pt-4">
          {brokers.map((broker) => (
            <Card 
              key={broker.id}
              className={`p-4 flex items-center gap-4 ${
                broker.comingSoon 
                  ? 'bg-gray-50 border-dashed border-gray-300' 
                  : 'hover:bg-gray-50 cursor-pointer transition-colors'
              }`}
              onClick={() => handleBrokerClick(broker.id, broker.comingSoon)}
            >
              <div className="flex-shrink-0 ml-2">
                {broker.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{broker.name}</h3>
                <p className="text-sm text-gray-600">{broker.description}</p>
              </div>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};